import Highcharts from 'highcharts';
import { observer } from 'mobx-react';
import React from 'react';
import { translator } from 'core/localization';
import 'highcharts/modules/xrange';
import 'highcharts/modules/accessibility';
import { AntSelectOption } from 'controls/react/ant/ant-select/types';

const i18n = translator({
	'Fixed': { no: 'Fast' },
	'Gradient': { no: 'Gradient' },
	'Top to bottom': { no: 'Fra topp til bunn' },
	'Bottom to top': { no: 'Fra bunn til topp' },
	'Left to right': { no: 'Fra venstre til høyre' },
	'Right to left': { no: 'Fra høyre til venstre' },
	'Top-left to bottom-right': { no: 'Fra øverst venstre til nederst høyre' },
	'Top-right to bottom-left': { no: 'Fra øverst høyre til nederst venstre' },
	'Bottom-left to top-right': { no: 'Fra nederst venstre til øverst høyre' },
	'Bottom-right to top-left': { no: 'Fra nederst høyre til øverst venstre' },
});

export type HighchartsWrapperProps = {
	config: Highcharts.Options | ((container: HTMLDivElement) => Highcharts.Options);
	onClick?: () => void;
};

export const HighchartsWrapper: React.FC<HighchartsWrapperProps> = observer((props) => {
	const [chartContainer, setChartContainer] = React.useState<HTMLDivElement>();
	const resizerRef = React.useRef<ResizeObserver>(null);

	const chartRef = React.useRef<Highcharts.Chart>(null);

	const getConfig = React.useCallback(() => {
		if (typeof props.config == 'function') {
			if (chartContainer == null) {
				return null;
			}

			return props.config(chartContainer);
		} else {
			return props.config;
		}
	}, [props.config, chartContainer]);

	const onRef = React.useCallback(
		(container: HTMLDivElement) => {
			if (resizerRef.current && chartContainer) {
				resizerRef.current.unobserve(chartContainer);
			}

			if (resizerRef.current == null) {
				resizerRef.current = new ResizeObserver(() => {
					const rect = chartRef.current?.container?.parentElement?.getBoundingClientRect();
					if (!rect || rect.width <= 0 || rect.height <= 0) return;
					chartRef.current.setSize(rect.width, rect.height, false);
				});
			}

			setChartContainer(container);

			if (container) {
				resizerRef.current.observe(container);
			}
		},
		[chartContainer],
	);

	React.useEffect(() => {
		chartRef.current?.destroy();

		if (chartContainer == null) return;

		const configFromProps = getConfig();

		const chartConfig = Highcharts.merge(
			{
				accessibility: {
					enabled: false,
				},
				tooltip: {
					outside: true,
				},
				title: {
					text: configFromProps.title?.text ?? '',
				},
				chart: {
					renderTo: chartContainer,
					backgroundColor: null,
					events: () => {},
				},
				credits: {
					enabled: false,
				},
				exporting: {
					enabled: false,
				},
			} as Highcharts.Options,
			configFromProps,
		);
		chartRef.current = Highcharts.chart(chartConfig);

		const chart = chartRef.current;
		// container's final layout size (flex/mxGraph geometry) is not always settled yet at chart
		// creation time, so set it explicitly once more after the next paint.
		requestAnimationFrame(() => {
			if (chartRef.current !== chart) return;
			const rect = chart.container?.parentElement?.getBoundingClientRect();
			if (rect && rect.width > 0 && rect.height > 0) {
				chart.setSize(rect.width, rect.height, false);
			}
		});
	}, [getConfig, chartContainer]);

	return <div onClick={props.onClick} ref={onRef} style={{ width: '100%', height: '100%' }}></div>;
});

export enum ColorDescriptionType {
	Gradient = 'gradient',
	Fixed = 'fixed',
}

let colorDescriptionTypeOptions: AntSelectOption[];
export function getColorTypeOptions() {
	if (colorDescriptionTypeOptions == null) {
		colorDescriptionTypeOptions = [
			{
				label: i18n('Fixed'),
				value: ColorDescriptionType.Fixed,
			},
			{
				label: i18n('Gradient'),
				value: ColorDescriptionType.Gradient,
			},
		];
	}

	return colorDescriptionTypeOptions;
}

export enum GradientDirection {
	ToBottom = 'toBottom',
	ToTop = 'toTop',
	ToRight = 'toRight',
	ToLeft = 'toLeft',
	ToBottomRight = 'toBottomRight',
	ToBottomLeft = 'toBottomLeft',
	ToTopRight = 'toTopRight',
	ToTopLeft = 'toTopLeft',
}

const gradientDirectionCoordinates: Record<GradientDirection, Highcharts.LinearGradientColorObject> = {
	[GradientDirection.ToBottom]: { x1: 0, y1: 0, x2: 0, y2: 1 },
	[GradientDirection.ToTop]: { x1: 0, y1: 1, x2: 0, y2: 0 },
	[GradientDirection.ToRight]: { x1: 0, y1: 0, x2: 1, y2: 0 },
	[GradientDirection.ToLeft]: { x1: 1, y1: 0, x2: 0, y2: 0 },
	[GradientDirection.ToBottomRight]: { x1: 0, y1: 0, x2: 1, y2: 1 },
	[GradientDirection.ToBottomLeft]: { x1: 1, y1: 0, x2: 0, y2: 1 },
	[GradientDirection.ToTopRight]: { x1: 0, y1: 1, x2: 1, y2: 0 },
	[GradientDirection.ToTopLeft]: { x1: 1, y1: 1, x2: 0, y2: 0 },
};

let gradientDirectionOptions: AntSelectOption[];
export function getGradientDirectionOptions() {
	if (gradientDirectionOptions == null) {
		gradientDirectionOptions = [
			{ label: i18n('Top to bottom'), value: GradientDirection.ToBottom },
			{ label: i18n('Bottom to top'), value: GradientDirection.ToTop },
			{ label: i18n('Left to right'), value: GradientDirection.ToRight },
			{ label: i18n('Right to left'), value: GradientDirection.ToLeft },
			{ label: i18n('Top-left to bottom-right'), value: GradientDirection.ToBottomRight },
			{ label: i18n('Top-right to bottom-left'), value: GradientDirection.ToBottomLeft },
			{ label: i18n('Bottom-left to top-right'), value: GradientDirection.ToTopRight },
			{ label: i18n('Bottom-right to top-left'), value: GradientDirection.ToTopLeft },
		];
	}

	return gradientDirectionOptions;
}

export type ColorDescription = {
	type: ColorDescriptionType;
	startColor: string;
	finishColor: string;
	direction?: GradientDirection;
};

export function buildBackground(color: ColorDescription): Partial<Highcharts.PaneBackgroundOptions> {
	if (color.type == ColorDescriptionType.Fixed) {
		return {
			backgroundColor: color.startColor,
		};
	}

	return {
		backgroundColor: {
			linearGradient: gradientDirectionCoordinates[color.direction ?? GradientDirection.ToBottom],
			stops: [
				[0, color.startColor],
				[1, color.finishColor],
			],
		},
	};
}

export type FillGradientDescription = {
	enabled: boolean;
	direction?: GradientDirection;
	startColor?: string;
	finishColor?: string;
};

export function buildFillColor(gradient?: FillGradientDescription): Highcharts.ColorType | undefined {
	if (!gradient?.enabled || !gradient.startColor || !gradient.finishColor) {
		return undefined;
	}

	return {
		linearGradient: gradientDirectionCoordinates[gradient.direction ?? GradientDirection.ToBottom],
		stops: [
			[0, gradient.startColor],
			[1, gradient.finishColor],
		],
	};
}
